import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  ShieldCheck, User, LogOut, Loader2, KeyRound, ShoppingBag,
  Pencil, Save, X, ArrowLeft, CheckCircle2, Mail, Phone,
  Fingerprint, CalendarDays, Sparkles, Cake, Wallet, Mic2,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import KakoAuthModal from '../components/KakoAuthModal';
import { KakoFollowCheck } from '../components/KakoFollowCheck';
import { KakoIdentity } from '../components/KakoIdentity';
import HostPanelTab from '../components/customer/HostPanelTab';
import BlueCoinsCard from '../components/BlueCoinsCard';
import TermsDialog from '../components/TermsDialog';
import VeracityDialog from '../components/VeracityDialog';
import { Checkbox } from '../components/ui/checkbox';
import { PhoneInput } from '../components/PhoneInput';
import { CountrySelect } from '../components/CountrySelect';
import { fetchFiscalLabels, docInfo, docValid } from '../lib/fiscalDocs';
import { splitWhatsapp, formatWhatsapp } from '../lib/countryDials';
import { CustomerPasswordChange } from '../components/customer/CustomerPasswordChange';
import { CustomerFiscalChange } from '../components/customer/CustomerFiscalChange';
import chestPurple from '../assets/images/chest-purple.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/customer-auth`;

const formatError = (detail) => {
  if (!detail) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  return String(detail);
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso || '-'; }
};

const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch { return '-'; }
};

const fmtCpf = (cpf) => cpf ? `${cpf.slice(0, 3)}.***.***-${cpf.slice(-2)}` : '-';
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtBirth = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};
// Validação de CPF (dígitos verificadores)
const cpfValido = (cpf) => {
  cpf = (cpf || '').replace(/\D/g, '');
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  for (const i of [9, 10]) {
    let s = 0;
    for (let j = 0; j < i; j++) s += Number(cpf[j]) * ((i + 1) - j);
    if (((s * 10) % 11) % 10 !== Number(cpf[i])) return false;
  }
  return true;
};

// Nome completo: exige nome E sobrenome (≥2 palavras com ≥2 letras), sem números
const nomeCompletoValido = (name) => {
  name = (name || '').trim();
  if (/\d/.test(name)) return false;
  return name.split(/\s+/).filter((p) => p.length >= 2).length >= 2;
};

// Nome aceita letras, espaço, hífen e apóstrofo (nomes estrangeiros)
const limpaNome = (v) => v.toUpperCase().replace(/[^A-ZÀ-ÖØ-Þ \-']/g, '');

// Data DD/MM/AAAA: retorna mensagem de erro ou null se válida
const validarNascimento = (birth) => {
  const d = (birth || '').replace(/\D/g, '');
  if (d.length !== 8) return 'Digite a data de nascimento completa (DD/MM/AAAA).';
  const dia = Number(d.slice(0, 2)), mes = Number(d.slice(2, 4)), ano = Number(d.slice(4));
  const dt = new Date(ano, mes - 1, dia);
  if (dt.getFullYear() !== ano || dt.getMonth() !== mes - 1 || dt.getDate() !== dia) {
    return 'Data de nascimento inválida. Confira dia, mês e ano.';
  }
  if (ano < 1900 || dt > new Date()) return 'Data de nascimento inválida.';
  return null;
};

const maskBirth = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const STATUS = {
  pending: { label: 'Aguardando', cls: 'bg-orange-100 text-orange-700 border border-orange-200' },
  paid: { label: 'Pago', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  completed: { label: 'Creditado', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  cancelled: { label: 'Cancelado', cls: 'bg-rose-100 text-rose-600 border border-rose-200' },
  failed: { label: 'Falhou', cls: 'bg-rose-100 text-rose-700 border border-rose-200' },
};

const COIN_IMG = 'https://customer-assets.emergentagent.com/job_8f198eda-3cd9-45f6-a741-5b80db2d25c7/artifacts/uhy6yxmh_ChatGPT%20Image%2015%20de%20fev.%20de%202026%2C%2013_18_27.png';

const GLASS = 'bg-white/80 backdrop-blur-xl border border-white/60 shadow-[0_8px_32px_rgba(124,58,237,0.08)] rounded-2xl';
const CTA = 'rounded-full bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white shadow-md shadow-orange-300/50 transition-all duration-200 ease-out hover:-translate-y-0.5 active:scale-[0.98]';
const SOFT_INPUT = 'rounded-xl bg-white/70 border-slate-200 focus:bg-white focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:border-transparent';

const InfoChip = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white/60 border border-slate-100 p-3 transition-all duration-200 ease-out hover:bg-white hover:shadow-sm">
    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-100 to-purple-100 text-purple-600">
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="truncate text-sm font-medium text-slate-800" data-testid={testId}>{value}</p>
    </div>
  </div>
);

export default function CustomerAccount() {
  const [checking, setChecking] = useState(true);
  const [account, setAccount] = useState(null);
  const [orders, setOrders] = useState([]);
  const [roulette, setRoulette] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // login
  const [loginId, setLoginId] = useState('');
  const [loginPass, setLoginPass] = useState('');

  // cadastro
  const [regId, setRegId] = useState('');
  const [regToken, setRegToken] = useState('');
  const [regLinkToken, setRegLinkToken] = useState(''); // link mágico do bot (sem OTP)
  const [regModal, setRegModal] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [regForm, setRegForm] = useState({ name: '', cpf: '', whatsapp: '', whatsappDial: '55', email: '', birth: '', password: '', confirm: '', terms: false, veracity: false, isForeigner: false, country: '', fiscalDocument: '' });
  const [isAgencyHost, setIsAgencyHost] = useState(false);
  const [fiscalLabels, setFiscalLabels] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [veracityOpen, setVeracityOpen] = useState(false);

  // recuperar senha
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotId, setForgotId] = useState('');
  const [forgotToken, setForgotToken] = useState('');
  const [forgotModal, setForgotModal] = useState(false);
  const [forgotPass, setForgotPass] = useState('');
  const [forgotDone, setForgotDone] = useState(false);

  // edição de perfil
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', whatsapp: '', whatsappDial: '55', email: '' });

  // filtro de datas dos pedidos
  const [dateRange, setDateRange] = useState(undefined);
  const [dateOpen, setDateOpen] = useState(false);

  const token = () => localStorage.getItem('customer_token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const loadAccount = useCallback(async () => {
    if (!token()) { setChecking(false); return; }
    try {
      const me = await axios.get(`${API}/me`, { headers: authHeaders() });
      setAccount(me.data);
      const ord = await axios.get(`${API}/me/orders`, { headers: authHeaders() });
      setOrders(ord.data.orders || []);
    } catch {
      localStorage.removeItem('customer_token');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { loadAccount(); }, [loadAccount]);

  // Link mágico de cadastro vindo do bot (?reg=TOKEN) — dispensa OTP
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reg = params.get('reg');
    if (!reg) return;
    window.history.replaceState({}, '', window.location.pathname);
    axios.get(`${API}/register-link/${encodeURIComponent(reg)}`)
      .then((r) => {
        setRegLinkToken(reg);
        setRegId(r.data.kako_id);
        setAuthTab('register');
        setError('');
      })
      .catch((e) => {
        const status = e.response?.status;
        setAuthTab(status === 409 ? 'login' : 'register');
        setError(formatError(e.response?.data?.detail) || 'Link de cadastro inválido.');
      });
  }, []);

  // Estado público da roleta (atalho quando evento ativo)
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/roulette/state`)
      .then((r) => setRoulette(r.data)).catch(() => setRoulette(null));
  }, []);

  // OIL TYCOON — Season visível? (atalho no painel; encerrada = só consulta)
  const [oilActive, setOilActive] = useState(false);
  const [oilEnded, setOilEnded] = useState(false);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/oil/rankings`)
      .then((r) => { setOilActive(true); setOilEnded(!!r.data?.ended); }).catch(() => setOilActive(false));
  }, []);

  // WITCH'S BALLOON — evento no ar? (atalho no painel)
  const [wbEvent, setWbEvent] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/wb/state`)
      .then((r) => setWbEvent(['ACTIVE', 'PAUSED', 'ENDED'].includes(r.data?.event?.status) ? r.data : null))
      .catch(() => setWbEvent(null));
  }, []);

  // RECARGA PREMIADA — baú por recarga, presente entregue na live
  const [rp, setRp] = useState(null);
  useEffect(() => {
    // no ar → aparece; encerrado → só se o cliente ainda tiver prêmio pendente para receber
    const tok = localStorage.getItem('customer_token');
    axios.get(`${BACKEND_URL}/api/rp/state`, tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined)
      .then((r) => { const ev = r.data?.event; setRp(ev && (ev.live || ev.upcoming || (r.data.me?.pending || 0) > 0) ? { ...ev, pending: r.data.me?.pending || 0 } : null); })
      .catch(() => setRp(null));
  }, []);

  // BAÚ MISTERIOSO — aparece quando o admin mostra para o público (ativo ou encerrado em consulta)
  const [mystery, setMystery] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/mystery-box/public-status`)
      .then((r) => setMystery(r.data?.visible ? r.data : null)).catch(() => setMystery(null));
  }, []);

  // GB ARENA — temporada ativa + saldo de diamantes do cliente
  const [arena, setArena] = useState(null);
  useEffect(() => {
    if (!account?.kako_id) { setArena(null); return; }
    axios.get(`${BACKEND_URL}/api/gb/state`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('customer_token')}` } })
      .then((r) => setArena(r.data?.active ? r.data : null)).catch(() => setArena(null));
  }, [account?.kako_id]);

  // Avatar do KAKO (opcional, silencioso)
  useEffect(() => {
    if (!account?.kako_id) { setAvatarUrl(''); return; }
    axios.post(`${BACKEND_URL}/api/kako/verify-user`, { kako_id: account.kako_id })
      .then((r) => setAvatarUrl(r.data?.user?.avatar || ''))
      .catch(() => setAvatarUrl(''));
  }, [account?.kako_id]);

  // É host da Fox Agency? (exibe a aba Painel do Host)
  useEffect(() => {
    if (!account?.kako_id) { setIsHost(false); return; }
    const t = localStorage.getItem('customer_token');
    axios.get(`${BACKEND_URL}/api/host-panel/access`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setIsHost(!!r.data?.is_host))
      .catch(() => setIsHost(false));
  }, [account?.kako_id]);

  // Cadastro internacional: só aparece se o KAKO ID verificado é host da agência
  useEffect(() => {
    if (!(regToken || regLinkToken) || !regId) { setIsAgencyHost(false); return; }
    axios.get(`${API}/agency-member/${encodeURIComponent(regId)}`)
      .then((r) => setIsAgencyHost(!!r.data?.is_member))
      .catch(() => setIsAgencyHost(false));
    fetchFiscalLabels().then(setFiscalLabels);
  }, [regToken, regLinkToken, regId]);

  const filteredOrders = useMemo(() => {
    if (!dateRange?.from) return orders;
    const from = new Date(dateRange.from); from.setHours(0, 0, 0, 0);
    const to = new Date(dateRange.to || dateRange.from); to.setHours(23, 59, 59, 999);
    return orders.filter((o) => {
      const d = new Date(o.created_at);
      return d >= from && d <= to;
    });
  }, [orders, dateRange]);

  const summary = useMemo(() => ({ count: filteredOrders.length }), [filteredOrders]);

  const dateLabel = dateRange?.from
    ? `${format(dateRange.from, 'dd/MM/yy')}${dateRange.to ? ` – ${format(dateRange.to, 'dd/MM/yy')}` : ''}`
    : 'Filtrar por data';

  const setPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateRange({ from, to });
    setDateOpen(false);
  };

  const onAuthSuccess = (data) => {
    localStorage.setItem('customer_token', data.access_token);
    setAccount(data.account);
    setError('');
    axios.get(`${API}/me/orders`, { headers: { Authorization: `Bearer ${data.access_token}` } })
      .then((r) => setOrders(r.data.orders || [])).catch(() => {});
  };

  const doLogin = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/login`, { kako_id: loginId.trim(), password: loginPass });
      onAuthSuccess(res.data);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doRegister = async () => {
    if (!nomeCompletoValido(regForm.name)) { setError('Informe seu nome completo (nome e sobrenome).'); return; }
    if (regForm.isForeigner) {
      if (!regForm.country) { setError('Escolha seu país.'); return; }
      if (!docValid(fiscalLabels, regForm.country, regForm.fiscalDocument)) {
        const r = docInfo(fiscalLabels, regForm.country);
        setError(`${r.label} inválido. Formato aceito: ${r.hint}.`); return;
      }
    } else if (!cpfValido(regForm.cpf)) {
      setError('CPF inválido. Confira os números digitados.'); return;
    }
    const birthErr = validarNascimento(regForm.birth);
    if (birthErr) { setError(birthErr); return; }
    if (regForm.password !== regForm.confirm) { setError('As senhas não conferem.'); return; }
    const waLen = regForm.whatsapp.length;
    if (regForm.whatsappDial === '55' ? (waLen < 10 || waLen > 11) : (regForm.whatsappDial.length + waLen < 8)) {
      setError(regForm.whatsappDial === '55' ? 'WhatsApp inválido. Use DDD + número.' : 'WhatsApp inválido. Informe o número completo do seu país.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(regForm.email)) { setError('Digite um e-mail válido.'); return; }
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/register`, {
        kako_id: regId.trim(),
        ...(regLinkToken ? { register_link_token: regLinkToken } : { verification_token: regToken }),
        name: regForm.name,
        ...(regForm.isForeigner
          ? { is_foreigner: true, country: regForm.country, fiscal_document: regForm.fiscalDocument }
          : { cpf: regForm.cpf }),
        whatsapp: regForm.whatsapp,
        whatsapp_dial: regForm.whatsappDial,
        email: regForm.email, birth_date: regForm.birth, password: regForm.password,
        terms_accepted: regForm.terms,
        veracity_accepted: regForm.veracity,
      });
      onAuthSuccess(res.data);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doReset = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/reset-password`, {
        kako_id: forgotId.trim(), verification_token: forgotToken, new_password: forgotPass,
      });
      setForgotDone(true);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doSaveProfile = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.patch(`${API}/me`, {
        name: editForm.name, whatsapp: editForm.whatsapp,
        whatsapp_dial: editForm.whatsappDial, email: editForm.email,
      }, { headers: authHeaders() });
      setAccount(res.data);
      setEditing(false);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doLogout = () => {
    localStorage.removeItem('customer_token');
    setAccount(null); setOrders([]);
    setLoginId(''); setLoginPass('');
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-purple-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-blue-50 p-4 sm:p-8">
      <div className="max-w-2xl mx-auto py-4 space-y-5">
        <div className="flex items-center justify-between">
          <a href="/" className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-white/80 px-3.5 py-1.5 text-sm font-semibold text-orange-600 shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-orange-50 hover:shadow-md"
            data-testid="account-back-home">
            <ArrowLeft className="w-4 h-4" /> Voltar à loja
          </a>
          {account && (
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="account-logout-button"
              className="rounded-full border-rose-200 bg-rose-50/80 text-rose-600 font-semibold transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-rose-100 hover:border-rose-300 hover:text-rose-700">
              <LogOut className="w-4 h-4 mr-1" /> Sair
            </Button>
          )}
        </div>

        {!account ? (
          <Card data-testid="customer-auth-card" className={GLASS}>
            <CardHeader className="text-center">
              <CardTitle className="font-display flex items-center justify-center gap-2 text-2xl tracking-tight text-slate-900">
                <User className="w-5 h-5 text-orange-500" /> Conta do Cliente
              </CardTitle>
              <CardDescription>
                Sua conta única e verificada — acompanhe pedidos e compre mais rápido.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {forgotMode ? (
                <div className="space-y-3" data-testid="forgot-password-form">
                  <p className="font-medium text-sm flex items-center gap-1">
                    <KeyRound className="w-4 h-4 text-orange-500" /> Recuperar senha
                  </p>
                  {forgotDone ? (
                    <div className="text-center space-y-3 py-4">
                      <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                      <p className="text-sm">Senha alterada com sucesso! Faça login com a nova senha.</p>
                      <Button className={CTA} data-testid="forgot-back-login"
                        onClick={() => { setForgotMode(false); setForgotDone(false); setForgotToken(''); setForgotPass(''); }}>
                        Ir para o login
                      </Button>
                    </div>
                  ) : !forgotToken ? (
                    <>
                      <p className="text-xs text-gray-600">
                        Confirme que a conta KAKO é sua: enviaremos um código no chat do app.
                      </p>
                      <Input data-testid="forgot-id-input" value={forgotId} inputMode="numeric" className={SOFT_INPUT}
                        onChange={(e) => setForgotId(e.target.value.replace(/\D/g, ''))}
                        placeholder="Seu KAKO ID" />
                      <Button className={`w-full ${CTA}`} disabled={!forgotId || busy}
                        data-testid="forgot-verify-button" onClick={() => setForgotModal(true)}>
                        <ShieldCheck className="w-4 h-4 mr-2" /> Verificar no chat do app
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
                        ✅ Conta verificada! Agora defina sua nova senha.
                      </p>
                      <Input data-testid="forgot-new-password" type="password" value={forgotPass} className={SOFT_INPUT}
                        onChange={(e) => setForgotPass(e.target.value)}
                        placeholder="Nova senha (mín. 8 caracteres)" />
                      <Button className={`w-full ${CTA}`}
                        disabled={forgotPass.length < 8 || busy}
                        data-testid="forgot-submit-button" onClick={doReset}>
                        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Salvar nova senha
                      </Button>
                    </>
                  )}
                  {error && <p className="text-sm text-red-600" data-testid="account-error">{error}</p>}
                  {!forgotDone && (
                    <button className="text-xs text-gray-500 underline" data-testid="forgot-cancel"
                      onClick={() => { setForgotMode(false); setError(''); }}>
                      Voltar ao login
                    </button>
                  )}
                </div>
              ) : (
                <Tabs value={authTab} onValueChange={(v) => { setAuthTab(v); setError(''); }}>
                  <TabsList className="grid w-full grid-cols-2 rounded-full bg-white/60 p-1">
                    <TabsTrigger value="login" data-testid="tab-login" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white">Entrar</TabsTrigger>
                    <TabsTrigger value="register" data-testid="tab-register" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white">Criar conta</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login" className="space-y-3 mt-4">
                    <Input data-testid="login-id-input" value={loginId} inputMode="numeric" className={SOFT_INPUT}
                      onChange={(e) => setLoginId(e.target.value.replace(/\D/g, ''))}
                      placeholder="Seu KAKO ID" />
                    <KakoIdentity kakoId={loginId} testId="login-kako-identity" />
                    <Input data-testid="login-password-input" type="password" value={loginPass} className={SOFT_INPUT}
                      onChange={(e) => setLoginPass(e.target.value)} placeholder="Sua senha"
                      onKeyDown={(e) => e.key === 'Enter' && doLogin()} />
                    {error && <p className="text-sm text-red-600" data-testid="account-error">{error}</p>}
                    <Button className={`w-full ${CTA}`}
                      disabled={!loginId || !loginPass || busy}
                      data-testid="login-submit-button" onClick={doLogin}>
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <User className="w-4 h-4 mr-2" />}
                      Entrar
                    </Button>
                    <button className="text-xs text-gray-500 underline w-full text-center"
                      data-testid="forgot-password-link"
                      onClick={() => { setForgotMode(true); setError(''); }}>
                      Esqueci minha senha
                    </button>
                  </TabsContent>

                  <TabsContent value="register" className="space-y-3 mt-4">
                    {!(regToken || regLinkToken) ? (
                      <>
                        <p className="text-xs text-gray-600">
                          Passo 1: confirme que a conta KAKO é sua. Enviaremos um código
                          de 6 dígitos no chat do app.
                        </p>
                        <Input data-testid="register-id-input" value={regId} inputMode="numeric" className={SOFT_INPUT}
                          onChange={(e) => setRegId(e.target.value.replace(/\D/g, ''))}
                          placeholder="Seu KAKO ID" />
                        {error && <p className="text-sm text-red-600" data-testid="account-error">{error}</p>}
                        <KakoFollowCheck key={regId} kakoId={regId} ctaClass={CTA}
                          onReady={() => setRegModal(true)}
                          onGoLogin={() => { setLoginId(regId); setAuthTab('login'); setError(''); }} />
                      </>
                    ) : (
                      <div className="space-y-3" data-testid="register-form">
                        <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
                          {regLinkToken
                            ? <span data-testid="register-link-verified">✅ KAKO ID <b>{regId}</b> verificado pelo chat do app! Complete seus dados — sem código. ✨</span>
                            : <>✅ KAKO ID <b>{regId}</b> verificado! Passo 2: complete seus dados.</>}
                        </p>
                        <Input data-testid="register-name-input" value={regForm.name} className={`${SOFT_INPUT} uppercase`}
                          onChange={(e) => setRegForm({ ...regForm, name: limpaNome(e.target.value) })}
                          placeholder="NOME COMPLETO (NOME E SOBRENOME)" />
                        <div className="flex items-start gap-2.5 rounded-xl border border-purple-200 bg-purple-50/70 p-2.5"
                          data-testid="register-foreigner-box">
                          <Checkbox id="foreigner-checkbox" data-testid="register-foreigner-checkbox"
                            checked={regForm.isForeigner} className="mt-0.5"
                            onCheckedChange={(v) => setRegForm({ ...regForm, isForeigner: !!v, cpf: '', country: '', fiscalDocument: '' })} />
                          <p className="select-none text-xs leading-relaxed text-purple-800 cursor-pointer"
                            onClick={() => setRegForm({ ...regForm, isForeigner: !regForm.isForeigner, cpf: '', country: '', fiscalDocument: '' })}>
                            <b>Moro fora do Brasil</b> — não tenho CPF. Vou informar meu país e o
                            documento fiscal de lá {isAgencyHost && <span className="text-purple-600">(host da agência)</span>}
                          </p>
                        </div>
                        {regForm.isForeigner ? (
                          <div className="space-y-3" data-testid="register-foreign-fields">
                            <CountrySelect testIdPrefix="register-country" exclude={['Brasil']} value={regForm.country}
                              onChange={(country) => setRegForm({ ...regForm, country })} />
                            <Input data-testid="register-fiscal-doc-input" value={regForm.fiscalDocument}
                              className={`${SOFT_INPUT} uppercase`} disabled={!regForm.country}
                              onChange={(e) => setRegForm({ ...regForm, fiscalDocument: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                              placeholder={regForm.country
                                ? docInfo(fiscalLabels, regForm.country).label
                                : 'Escolha o país primeiro'} />
                            {regForm.country && (
                              <p className="text-[11px] text-slate-500" data-testid="register-fiscal-doc-hint">
                                {docInfo(fiscalLabels, regForm.country).label} · {docInfo(fiscalLabels, regForm.country).hint}
                              </p>
                            )}
                            {fiscalLabels && regForm.country && regForm.fiscalDocument
                              && !docValid(fiscalLabels, regForm.country, regForm.fiscalDocument) && (
                              <p className="text-[11px] font-medium text-rose-600" data-testid="register-fiscal-doc-error">
                                {docInfo(fiscalLabels, regForm.country).label} de {regForm.country} inválido · {docInfo(fiscalLabels, regForm.country).hint}
                              </p>
                            )}
                          </div>
                        ) : (
                          <Input data-testid="register-cpf-input" value={regForm.cpf} inputMode="numeric" className={SOFT_INPUT}
                            onChange={(e) => setRegForm({ ...regForm, cpf: e.target.value.replace(/[^\d.-]/g, '') })}
                            placeholder="CPF (só números)" />
                        )}
                        <Input data-testid="register-birthdate-input" value={regForm.birth} inputMode="numeric" className={SOFT_INPUT}
                          maxLength={10}
                          onChange={(e) => setRegForm({ ...regForm, birth: maskBirth(e.target.value) })}
                          placeholder="Data de nascimento (DD/MM/AAAA)" />
                        <PhoneInput testIdPrefix="register-whatsapp" inputClassName={SOFT_INPUT}
                          countryName={regForm.isForeigner ? regForm.country : 'Brasil'}
                          dial={regForm.whatsappDial} number={regForm.whatsapp}
                          onChange={({ dial, number }) => setRegForm({ ...regForm, whatsappDial: dial, whatsapp: number })} />
                        <Input data-testid="register-email-input" type="email" inputMode="email" value={regForm.email} className={SOFT_INPUT}
                          onChange={(e) => setRegForm({ ...regForm, email: e.target.value.toLowerCase().trim() })}
                          placeholder="E-mail" />
                        <Input data-testid="register-password-input" type="password" value={regForm.password} className={SOFT_INPUT}
                          onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                          placeholder="Senha (mín. 8 caracteres)" />
                        <Input data-testid="register-confirm-input" type="password" value={regForm.confirm} className={SOFT_INPUT}
                          onChange={(e) => setRegForm({ ...regForm, confirm: e.target.value })}
                          placeholder="Confirmar senha" />
                        <p className="text-[11px] leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5"
                          data-testid="register-legal-warning">
                          ⚠️ Preencha seus dados oficiais corretamente. Dados incorretos, falsos ou
                          fraudulentos são passíveis de bloqueio da conta e da aplicação das medidas
                          legais cabíveis.
                        </p>
                        <div className="flex items-start gap-2.5 pt-1">
                          <Checkbox id="terms-checkbox" data-testid="register-terms-checkbox"
                            checked={regForm.terms} className="mt-0.5"
                            onCheckedChange={(v) => setRegForm({ ...regForm, terms: !!v })} />
                          <p className="text-xs text-gray-600 leading-relaxed select-none">
                            <span className="cursor-pointer" onClick={() => setRegForm({ ...regForm, terms: !regForm.terms })}>
                              Li e estou ciente do{' '}
                            </span>
                            <button type="button" data-testid="open-terms-link"
                              onClick={(e) => { e.stopPropagation(); setTermsOpen(true); }}
                              className="text-blue-600 underline underline-offset-2 hover:text-blue-800 font-medium">
                              Termo de Tratamento de Dados e da Política de Privacidade
                            </button>
                            <span className="cursor-pointer" onClick={() => setRegForm({ ...regForm, terms: !regForm.terms })}>
                              {' '}da FOX SELLER.
                            </span>
                          </p>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <Checkbox id="veracity-checkbox" data-testid="register-veracity-checkbox"
                            checked={regForm.veracity} className="mt-0.5"
                            onCheckedChange={(v) => setRegForm({ ...regForm, veracity: !!v })} />
                          <p className="text-xs text-gray-600 leading-relaxed select-none">
                            <span className="cursor-pointer" onClick={() => setRegForm({ ...regForm, veracity: !regForm.veracity })}>
                              Declaro que os dados cadastrais e fiscais informados são verdadeiros,
                              atualizados e correspondem aos meus dados oficiais. Estou ciente de que
                              informações falsas, fraudulentas ou uso indevido de dados de terceiros
                              poderão resultar no bloqueio da operação e na adoção das medidas legais
                              cabíveis, conforme a{' '}
                            </span>
                            <button type="button" data-testid="open-veracity-link"
                              onClick={(e) => { e.stopPropagation(); setVeracityOpen(true); }}
                              className="text-blue-600 underline underline-offset-2 hover:text-blue-800 font-medium">
                              Declaração de Veracidade dos Dados Cadastrais e Fiscais
                            </button>
                            <span className="cursor-pointer" onClick={() => setRegForm({ ...regForm, veracity: !regForm.veracity })}>
                              {' '}da FOX SELLER.
                            </span>
                          </p>
                        </div>
                        <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} />
                        <VeracityDialog open={veracityOpen} onOpenChange={setVeracityOpen} />
                        {error && <p className="text-sm text-red-600" data-testid="account-error">{error}</p>}
                        <Button className={`w-full ${CTA}`}
                          disabled={busy || !regForm.name || (regForm.isForeigner ? (!regForm.country || !docValid(fiscalLabels, regForm.country, regForm.fiscalDocument)) : !regForm.cpf) || !regForm.whatsapp || !regForm.email || regForm.birth.replace(/\D/g, '').length !== 8 || regForm.password.length < 8 || !regForm.terms || !regForm.veracity}
                          data-testid="register-submit-button" onClick={doRegister}>
                          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                          Criar minha conta
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5" data-testid="customer-account-panel">
            {/* ===== Cartão fixo de identidade ===== */}
            <Card data-testid="profile-info-card" className={`${GLASS} overflow-hidden`}>
              <div className="h-20 bg-gradient-to-r from-orange-400 via-orange-500 to-purple-500 relative">
                <Sparkles className="absolute right-4 top-4 h-5 w-5 text-white/60" />
              </div>
              <CardContent className="relative px-5 pb-5 pt-0 sm:px-6">
                <div className="-mt-10 flex items-end gap-3">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={account.nickname || account.name}
                      className="h-20 w-20 rounded-full border-4 border-white object-cover shadow-lg" />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-orange-400 to-purple-500 text-3xl font-black text-white shadow-lg font-display">
                      {(account.nickname || account.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
                    {account.nickname || account.name}
                  </h1>
                  <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2.5 py-0.5 text-[11px] font-bold text-purple-700"
                    data-testid="profile-verified-badge">
                    <ShieldCheck className="h-3 w-3" /> Verificada
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-mono font-semibold text-slate-700"
                    data-testid="profile-kako-id">
                    KAKO ID {account.kako_id}
                  </span>
                  membro desde {fmtDay(account.created_at)}
                </p>
              </CardContent>
            </Card>

            {/* ===== Atalho da Roleta (só com evento ativo) ===== */}
            {roulette?.active && (
              <a href="/roleta" data-testid="account-roulette-shortcut" className="block">
                <div className="relative overflow-hidden rounded-2xl px-5 py-4 shadow-lg transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-xl"
                  style={{ background: 'linear-gradient(90deg, #16247a 0%, #0b1240 100%)', border: '1px solid rgba(255,213,74,.45)' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="text-3xl">🎡</span>
                      <div className="min-w-0">
                        <p className="text-sm font-black text-yellow-300">A Roleta das Azulzinhas está rolando!</p>
                        <p className="text-[11px] text-blue-200">
                          Jackpot acumulado: <b className="text-yellow-300">{Number(roulette.jackpot_value || 0).toLocaleString('pt-BR')}</b> moedas · 500 Azulzinhas = 1 giro
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 px-4 py-1.5 text-xs font-black text-slate-900 shadow-md">
                      Girar agora
                    </span>
                  </div>
                </div>
              </a>
            )}

            {/* ===== Atalho OIL TYCOON (só com Season ativa) ===== */}
            {oilActive && (
              <a href="/tycoon" data-testid="account-tycoon-shortcut" className="block">
                <div className="relative overflow-hidden rounded-2xl px-5 py-4 shadow-lg transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-xl"
                  style={{ background: 'linear-gradient(90deg, #1a1206 0%, #0a0d12 100%)', border: '1px solid rgba(245,158,11,.5)' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="text-3xl">🛢️</span>
                      <div className="min-w-0">
                        <p className="text-sm font-black text-amber-400" data-testid="account-tycoon-title">
                          {oilEnded ? 'OIL TYCOON — Season encerrada' : 'OIL TYCOON — a Season está ATIVA!'}
                        </p>
                        <p className="text-[11px] text-zinc-400">
                          {oilEnded
                            ? 'Veja o resultado final: Top Magnatas, Acionistas e o Jackpot da Season'
                            : 'Construa seu império do petróleo, domine a máfia e dispute o Jackpot dos Magnatas'}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-1.5 text-xs font-black text-slate-900 shadow-md">
                      {oilEnded ? 'Ver resultados' : 'Jogar'}
                    </span>
                  </div>
                </div>
              </a>
            )}

            {/* ===== Atalho WITCH'S BALLOON (só com evento ativo) ===== */}
            {wbEvent && (
              <a href="/witch-balloon" data-testid="account-wb-shortcut" className="block">
                <div className="relative overflow-hidden rounded-2xl px-5 py-4 shadow-lg transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-xl"
                  style={{ background: 'linear-gradient(90deg, #2a0f4a 0%, #0a0612 100%)', border: '1px solid rgba(249,115,22,.55)' }}>
                  <img src="/wb/balloon.webp" alt="" className="pointer-events-none absolute -right-4 -top-6 h-28 w-28 object-contain opacity-30 blur-[1px]" />
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <img src="/wb/witch_cheer.webp" alt="" className="h-12 w-12 shrink-0 object-contain drop-shadow" />
                      <div className="min-w-0">
                        <p className="text-sm font-black text-orange-400" data-testid="account-wb-title">
                          {wbEvent.event?.status === 'ENDED' ? "WITCH'S BALLOON — evento encerrado" : "WITCH'S BALLOON — estoure balões e ganhe moedas!"}
                        </p>
                        <p className="text-[11px] text-purple-200/80">
                          {wbEvent.event?.status === 'ENDED'
                            ? 'Veja o ranking final, os prêmios ao vivo e o seu histórico de balões'
                            : <>Balão dourado de <b className="text-amber-300">{Number(wbEvent.config?.prize_big || 50000).toLocaleString('pt-BR')} moedas</b>
                              {' '}· dia {wbEvent.day_index} de {wbEvent.duration_days} · recarregue, junte Moedas da Bruxa e estoure balões</>}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-1.5 text-xs font-black text-slate-900 shadow-md">
                      {wbEvent.event?.status === 'ENDED' ? 'Ver resultados' : 'Jogar'}
                    </span>
                  </div>
                </div>
              </a>
            )}

            {/* ===== Atalho GB ARENA (só com temporada ativa) ===== */}
            {arena && (
              <a href="/arena" data-testid="account-arena-shortcut" className="block">
                <div className="relative overflow-hidden rounded-2xl px-5 py-4 shadow-lg transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-xl"
                  style={{ background: 'linear-gradient(90deg, #1a0b2e 0%, #14061f 100%)', border: '1px solid rgba(255,107,0,.5)' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <img src="/gb/mobile.png" alt="" className="h-11 w-11 shrink-0 object-contain drop-shadow" />
                      <div className="min-w-0">
                        <p className="text-sm font-black text-orange-400">
                          {(arena.season?.name || 'GB ARENA').toUpperCase()} — temporada ATIVA!
                        </p>
                        <p className="text-[11px] text-purple-200/80">
                          Você tem <b className="text-cyan-300">{Number(arena.player?.diamonds || 0).toLocaleString('pt-BR')} diamantes</b>
                          {' '}· está na fase <b className="text-orange-300">{arena.player?.progress?.node || 1}</b> de {(arena.config?.nodes || []).length}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full bg-gradient-to-r from-orange-400 to-amber-500 px-4 py-1.5 text-xs font-black text-slate-900 shadow-md">
                      Jogar
                    </span>
                  </div>
                </div>
              </a>
            )}

            {/* ===== Atalho RECARGA PREMIADA ===== */}
            {rp && (
              <a href="/recarga-premiada" data-testid="account-rp-shortcut" className="block">
                <div className="relative overflow-hidden rounded-2xl px-5 py-4 shadow-lg transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-xl"
                  style={{ backgroundImage: 'linear-gradient(90deg, rgba(59,7,100,.85) 0%, rgba(20,10,46,.75) 100%), url(/rp/bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid rgba(251,191,36,.5)' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <img src="/rp/chest.webp" alt="" className="h-14 w-14 shrink-0 object-contain drop-shadow-[0_0_10px_rgba(251,191,36,.8)]" />
                      <div className="min-w-0">
                        <p className="text-sm font-black text-amber-300" data-testid="account-rp-title">
                          {rp.live ? 'RECARGA PREMIADA — ganhe presentes na sua live!' : rp.upcoming ? 'RECARGA PREMIADA — EM BREVE' : 'RECARGA PREMIADA — evento encerrado'}
                        </p>
                        <p className="text-[11px] text-purple-200/80">
                          {rp.live ? <>A cada <b className="text-amber-300">{Number(rp.coins_per_chest).toLocaleString('pt-BR')} moedas</b> você abre um baú com presentes de até 10.000 🪙</>
                            : rp.upcoming ? <>Começa em <b className="text-lime-300">{new Date(rp.starts_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</b> — a cada {Number(rp.coins_per_chest).toLocaleString('pt-BR')} moedas, um baú</>
                              : `Você tem ${rp.pending} prêmio(s) para receber na live`}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full bg-gradient-to-r from-fuchsia-400 to-amber-300 px-4 py-1.5 text-xs font-black text-slate-900 shadow-md">{rp.live ? 'Abrir baú' : rp.upcoming ? 'Em breve' : 'Ver prêmios'}</span>
                  </div>
                </div>
              </a>
            )}

            {/* ===== Atalho BAÚ MISTERIOSO (switch "mostrar para o público") ===== */}
            {mystery && (
              <a href="/bau-misterioso" data-testid="account-mystery-shortcut" className="block">
                <div className="relative overflow-hidden rounded-2xl px-5 py-4 shadow-lg transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-xl"
                  style={{ background: 'linear-gradient(90deg, #2a0b45 0%, #12051f 100%)', border: '1px solid rgba(217,70,239,.5)' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <img src={chestPurple} alt="" className="h-11 w-11 shrink-0 object-contain drop-shadow" />
                      <div className="min-w-0">
                        <p className="text-sm font-black text-fuchsia-300" data-testid="account-mystery-title">
                          {mystery.ended ? 'BAÚ MISTERIOSO — evento encerrado' : 'BAÚ MISTERIOSO — evento ATIVO!'}
                        </p>
                        <p className="text-[11px] text-purple-200/80">
                          {mystery.ended
                            ? 'Veja o ranking final e os Top Sortudos da semana'
                            : <>Bata a meta diária de <b className="text-fuchsia-300">{Number(mystery.goal_1 || 0).toLocaleString('pt-BR')} moedas</b> e abra baús com prêmios misteriosos</>}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full bg-gradient-to-r from-fuchsia-400 to-purple-500 px-4 py-1.5 text-xs font-black text-slate-900 shadow-md">
                      {mystery.ended ? 'Ver resultados' : 'Abrir'}
                    </span>
                  </div>
                </div>
              </a>
            )}

            <Tabs defaultValue="wallet" className="w-full" data-testid="account-tabs">
              <TabsList className={`grid w-full ${isHost ? 'grid-cols-4' : 'grid-cols-3'} h-auto rounded-full bg-white/70 backdrop-blur-xl border border-white/60 p-1 shadow-sm mb-4`}>
                <TabsTrigger value="wallet" data-testid="account-tab-wallet"
                  className="rounded-full py-2 text-xs sm:text-sm font-semibold gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md">
                  <Wallet className="w-4 h-4" /> Carteira
                </TabsTrigger>
                <TabsTrigger value="orders" data-testid="account-tab-orders"
                  className="rounded-full py-2 text-xs sm:text-sm font-semibold gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md">
                  <ShoppingBag className="w-4 h-4" /> Meus Pedidos
                </TabsTrigger>
                <TabsTrigger value="profile" data-testid="account-tab-profile"
                  className="rounded-full py-2 text-xs sm:text-sm font-semibold gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md">
                  <User className="w-4 h-4" /> Meus Dados
                </TabsTrigger>
                {isHost && (
                  <TabsTrigger value="host" data-testid="account-tab-host"
                    className="rounded-full py-2 text-xs sm:text-sm font-semibold gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md">
                    <Mic2 className="w-4 h-4" /> <span className="hidden sm:inline">Painel do Host</span><span className="sm:hidden">Host</span>
                  </TabsTrigger>
                )}
              </TabsList>

              {/* ===== Aba Painel do Host (só hosts da Fox Agency) ===== */}
              {isHost && (
                <TabsContent value="host" className="mt-0" data-testid="account-host-tab">
                  <HostPanelTab />
                </TabsContent>
              )}

              {/* ===== Aba Carteira ===== */}
              <TabsContent value="wallet" className="mt-0 space-y-5" data-testid="account-wallet-tab">
                <BlueCoinsCard meUrl="/api/blue-coins/me" ledgerUrl="/api/blue-coins/me/ledger" tokenKey="customer_token" />
              </TabsContent>

              {/* ===== Aba Meus Dados ===== */}
              <TabsContent value="profile" className="mt-0" data-testid="account-profile-tab">
            <Card className={GLASS} data-testid="profile-data-card">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="font-display flex items-center gap-2 text-lg tracking-tight text-slate-900">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-100 to-purple-100 text-orange-600">
                      <User className="w-4 h-4" />
                    </span>
                    Meus Dados
                  </CardTitle>
                  {!editing && (
                    <Button size="sm" variant="outline" data-testid="profile-edit-button"
                      onClick={() => {
                        const wa = splitWhatsapp(account.whatsapp, account.whatsapp_dial);
                        setEditForm({ name: account.name, whatsapp: wa.number, whatsappDial: wa.dial, email: account.email || '' });
                        setEditing(true);
                      }}
                      className="rounded-full border-slate-200 bg-white/90 text-slate-600 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-purple-300 hover:text-purple-700">
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar dados
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {editing ? (
                  <div className="space-y-2.5" data-testid="profile-edit-form">
                    <Input data-testid="edit-name-input" value={editForm.name} className={`${SOFT_INPUT} uppercase`}
                      onChange={(e) => setEditForm({ ...editForm, name: limpaNome(e.target.value) })}
                      placeholder="Nome completo" />
                    <PhoneInput testIdPrefix="edit-whatsapp" inputClassName={SOFT_INPUT}
                      dial={editForm.whatsappDial} number={editForm.whatsapp}
                      onChange={({ dial, number }) => setEditForm({ ...editForm, whatsappDial: dial, whatsapp: number })} />
                    <Input data-testid="edit-email-input" type="email" inputMode="email" value={editForm.email} className={SOFT_INPUT}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value.toLowerCase().trim() })}
                      placeholder="E-mail" />
                    {error && <p className="text-sm text-red-600" data-testid="account-error">{error}</p>}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" className={CTA} disabled={busy}
                        data-testid="profile-save-button" onClick={doSaveProfile}>
                        <Save className="w-4 h-4 mr-1" /> Salvar
                      </Button>
                      <Button size="sm" variant="outline" data-testid="profile-cancel-button"
                        className="rounded-full"
                        onClick={() => { setEditing(false); setError(''); }}>
                        <X className="w-4 h-4 mr-1" /> Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <InfoChip icon={User} label="Nome" value={account.name} testId="profile-name" />
                    {account.is_foreigner ? (
                      <InfoChip icon={Fingerprint} label={account.country || 'Documento'}
                        value={account.fiscal_document || '-'} testId="profile-fiscal-doc" />
                    ) : (
                      <InfoChip icon={Fingerprint} label="CPF" value={fmtCpf(account.cpf)} testId="profile-cpf" />
                    )}
                    <InfoChip icon={Phone} label="WhatsApp" value={formatWhatsapp(account.whatsapp, account.whatsapp_dial)} testId="profile-whatsapp" />
                    <InfoChip icon={Mail} label="E-mail" value={account.email || '—'} testId="profile-email" />
                    {account.birth_date && (
                      <InfoChip icon={Cake} label="Nascimento" value={fmtBirth(account.birth_date)} testId="profile-birthdate" />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <div className="mt-4">
              <CustomerFiscalChange account={account} onUpdated={setAccount}
                cardClass={GLASS} inputClass={SOFT_INPUT} />
            </div>
            <div className="mt-4">
              <CustomerPasswordChange kakoId={account.kako_id} cardClass={GLASS} inputClass={SOFT_INPUT} />
            </div>
              </TabsContent>

              {/* ===== Aba Meus Pedidos ===== */}
              <TabsContent value="orders" className="mt-0" data-testid="account-orders-tab">
            <Card className={GLASS}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="font-display flex items-center gap-2 text-lg tracking-tight text-slate-900">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-100 to-purple-100 text-orange-600">
                      <ShoppingBag className="w-4 h-4" />
                    </span>
                    Meus pedidos
                  </CardTitle>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="date-filter-trigger"
                        className={`rounded-full border-slate-200 bg-white/80 text-xs transition-all duration-200 ease-out hover:-translate-y-0.5 ${dateRange?.from ? 'border-purple-300 text-purple-700' : 'text-slate-600'}`}>
                        <CalendarDays className="mr-1.5 h-3.5 w-3.5" />
                        {dateLabel}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-auto rounded-2xl border-slate-200 bg-white p-2 shadow-xl z-50">
                      <div className="flex flex-wrap gap-1.5 px-2 pt-2">
                        <button data-testid="date-preset-today" onClick={() => setPreset(0)}
                          className="rounded-full bg-orange-50 px-3 py-1 text-[11px] font-semibold text-orange-700 transition-colors hover:bg-orange-100">Hoje</button>
                        <button data-testid="date-preset-7d" onClick={() => setPreset(7)}
                          className="rounded-full bg-orange-50 px-3 py-1 text-[11px] font-semibold text-orange-700 transition-colors hover:bg-orange-100">7 dias</button>
                        <button data-testid="date-preset-30d" onClick={() => setPreset(30)}
                          className="rounded-full bg-orange-50 px-3 py-1 text-[11px] font-semibold text-orange-700 transition-colors hover:bg-orange-100">30 dias</button>
                        {dateRange?.from && (
                          <button data-testid="date-filter-clear" onClick={() => { setDateRange(undefined); setDateOpen(false); }}
                            className="rounded-full bg-rose-50 px-3 py-1 text-[11px] font-semibold text-rose-600 transition-colors hover:bg-rose-100">Limpar</button>
                        )}
                      </div>
                      <Calendar mode="range" selected={dateRange} onSelect={setDateRange}
                        numberOfMonths={1} locale={ptBR} data-testid="date-filter-calendar" />
                    </PopoverContent>
                  </Popover>
                </div>
                {orders.length > 0 && (
                  <p className="text-[11px] text-slate-500" data-testid="orders-summary">
                    {summary.count} {summary.count === 1 ? 'pedido' : 'pedidos'}
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {orders.length === 0 ? (
                  <div className="py-8 text-center" data-testid="orders-empty">
                    <img src={COIN_IMG} alt="Moedas" className="mx-auto mb-2 h-12 w-12 object-contain opacity-60" />
                    <p className="text-sm text-slate-500">
                      Nenhum pedido ainda. <a href="/" className="font-semibold text-orange-600 underline">Comprar moedas</a>
                    </p>
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className="py-8 text-center" data-testid="orders-filter-empty">
                    <CalendarDays className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                    <p className="text-sm text-slate-500">Nenhum pedido no período selecionado.</p>
                    <button className="mt-1 text-xs font-semibold text-purple-600 underline"
                      onClick={() => setDateRange(undefined)}>Limpar filtro</button>
                  </div>
                ) : (
                  <div className="custom-scrollbar max-h-[55vh] overflow-y-auto pr-2" data-testid="orders-scroll-container">
                    <div data-testid="orders-list">
                      {filteredOrders.map((o) => {
                        const st = STATUS[o.status] || { label: o.status, cls: 'bg-slate-100 text-slate-600 border border-slate-200' };
                        return (
                          <div key={o.id}
                            className="flex items-center justify-between gap-3 border-b border-slate-100 py-3.5 last:border-0 transition-colors duration-200 hover:bg-white/70 rounded-lg px-1.5"
                            data-testid={`order-item-${o.id.slice(0, 8)}`}>
                            <div className="flex min-w-0 items-center gap-3">
                              <img src={COIN_IMG} alt="Moedas"
                                className="h-10 w-10 flex-shrink-0 object-contain drop-shadow-sm" />
                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold text-slate-800">
                                  {((o.coins || 0) + (o.bonus_coins || 0)).toLocaleString('pt-BR')} moedas
                                </p>
                                <p className="text-[11px] text-slate-500">
                                  #{o.id.slice(0, 8).toUpperCase()} · {fmtDate(o.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <p className="text-sm font-bold text-slate-800">
                                {o.payment_method === 'mbway' && o.mbway_amount_eur
                                  ? `€ ${Number(o.mbway_amount_eur).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  : fmtBRL(o.amount)}
                              </p>
                              {o.payment_method === 'mbway' && o.mbway_amount_eur && (
                                <p className="text-[10px] text-slate-400" data-testid={`order-brl-${o.id.slice(0, 8)}`}>
                                  {fmtBRL(o.amount)}
                                </p>
                              )}
                              <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>
                                {st.label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      <KakoAuthModal
        open={regModal} onOpenChange={setRegModal}
        purpose="cadastro" initialKakoId={regId} lockKakoId autoSend
        title="Verificar sua conta KAKO"
        onVerified={(t) => { setRegToken(t); setRegModal(false); }}
      />
      <KakoAuthModal
        open={forgotModal} onOpenChange={setForgotModal}
        purpose="recuperar_senha" initialKakoId={forgotId} lockKakoId
        title="Confirmar que a conta é sua"
        onVerified={(t) => { setForgotToken(t); setForgotModal(false); }}
      />
    </div>
  );
}
